import {curriculumFromResponse} from './curriculum';
import {PedagogicalLevelFields} from './PedagogicalLevelFields';
import {curriculumChapterLabel} from './curriculum';
import { useEffect, useMemo, useState } from "react";
import { DndContext, DragEndEvent, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ArrowLeft, BookOpen, Check, ChevronDown, ChevronRight, GripVertical, Pencil, Plus, Save, Trash2 } from "lucide-react";
import {
  ActivityDefinition,
  CatalogCompetency,
  SchoolLevel,
  ValidationCriteria,
  orderCatalogForLevel,
} from "./catalog";
import { Curriculum, cloneCurriculum, curriculumForLevel, defaultCurriculum } from "./curriculum";
import "./activity-library.css";

type CatalogViewProps = {
  catalog: CatalogCompetency[];
  visibleLevels: SchoolLevel[];
  onSave: (competency: CatalogCompetency) => void;
  onDelete: (id: string) => void;
  onReorder: (activeId: string, overId: string) => void;
  onBack?: () => void;
};

const makeId = (prefix: string) => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
const blankCriteria = (): ValidationCriteria => ({ A: "", B: "", C: "", D: "" });

function SortableCatalogItem({ id, children, className = "" }: { id: string; children: React.ReactNode; className?: string }) {
  const sortable = useSortable({ id });
  return (
    <div
      ref={sortable.setNodeRef}
      style={{ transform: CSS.Transform.toString(sortable.transform), transition: sortable.transition }}
      className={`${className} sortable-catalog-item ${sortable.isDragging ? "dragging" : ""}`}
    >
      <button className="catalog-drag-handle" {...sortable.attributes} {...sortable.listeners} aria-label="Déplacer la compétence"><GripVertical size={17} /></button>
      {children}
    </div>
  );
}

export function CatalogView({ catalog, visibleLevels, onSave, onDelete, onReorder, onBack }: CatalogViewProps) {
  const [level, setLevel] = useState<SchoolLevel>(visibleLevels[0] ?? "Seconde");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [label, setLabel] = useState("");
  const [parentId, setParentId] = useState("");
  const [criteria, setCriteria] = useState<ValidationCriteria>(blankCriteria);
  const [showForm, setShowForm] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  useEffect(() => {
    if (!visibleLevels.includes(level) && visibleLevels[0]) setLevel(visibleLevels[0]);
  }, [level, visibleLevels]);

  const levelItems = orderCatalogForLevel(catalog, level);
  const generalItems = levelItems.filter((item) => !item.parentId);

  function clearForm() {
    setEditingId(null);
    setCode("");
    setLabel("");
    setParentId("");
    setCriteria(blankCriteria());
    setShowForm(false);
  }

  function edit(item: CatalogCompetency) {
    setEditingId(item.id);
    setLevel(item.schoolLevel);
    setCode(item.code);
    setLabel(item.label);
    setParentId(item.parentId ?? "");
    setCriteria({ ...item.criteria });
    setShowForm(true);
  }

  function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!code.trim() || !label.trim()) return;
    onSave({
      id: editingId ?? makeId("competency"),
      code: code.trim().toUpperCase(),
      label: label.trim(),
      schoolLevel: level,
      parentId: parentId || undefined,
      sortOrder: catalog.find((item) => item.id === editingId)?.sortOrder ?? levelItems.length,
      criteria,
    });
    clearForm();
  }

  return (
    <main className="management-page">
      <div className="management-heading">
        <div>{onBack && <button type="button" className="secondary management-back-button" onClick={onBack}><ArrowLeft size={16} /> Retour aux paramètres</button>}<span className="eyebrow">Référentiel pédagogique</span><h1>Catalogue de compétences</h1></div>
          <button className="primary" onClick={() => { clearForm(); setShowForm(true); }}><Plus size={17} /> Nouvelle compétence</button>
      </div>

      <div className="level-tabs" role="tablist" aria-label="Niveaux">
        {visibleLevels.map((item) => (
          <button key={item} className={level === item ? "active" : ""} onClick={() => setLevel(item)}>{item}</button>
        ))}
      </div>

      {showForm && (
        <form className="management-form competency-form" onSubmit={submit}>
          <div className="form-title"><strong>{editingId ? "Modifier la compétence" : "Ajouter une compétence"}</strong><button type="button" onClick={clearForm}>Annuler</button></div>
          <label><span>Niveau</span><select value={level} onChange={(event) => { setLevel(event.target.value as SchoolLevel); setParentId(""); }}>{visibleLevels.map((item) => <option key={item}>{item}</option>)}</select></label>
          <label><span>Code</span><input value={code} onChange={(event) => setCode(event.target.value)} placeholder="Ex. ANA.1" required /></label>
          <label className="wide-field"><span>Intitulé</span><input value={label} onChange={(event) => setLabel(event.target.value)} placeholder="Ex. Exploiter des données" required /></label>
          <label className="wide-field"><span>Rattachement</span><select value={parentId} onChange={(event) => setParentId(event.target.value)}><option value="">Compétence générale</option>{generalItems.filter((item) => item.id !== editingId).map((item) => <option key={item.id} value={item.id}>{item.code} — {item.label}</option>)}</select></label>
          <div className="criteria-editor">
            {(["A", "B", "C", "D"] as const).map((value) => <label key={value} className={`criterion-input criterion-${value.toLowerCase()}`}><span>Critère {value}</span><textarea value={criteria[value]} onChange={(event) => setCriteria({ ...criteria, [value]: event.target.value })} placeholder={`Ce que l’élève doit réussir pour obtenir ${value}`} /></label>)}
          </div>
          <button className="primary form-submit" type="submit"><Save size={16} /> Enregistrer</button>
        </form>
      )}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(event: DragEndEvent) => {
        if (!event.over || event.active.id === event.over.id) return;
        const active = catalog.find((item) => item.id === String(event.active.id));
        const over = catalog.find((item) => item.id === String(event.over?.id));
        if (!active || !over) return;
        const destinationId = !active.parentId && over.parentId ? over.parentId : over.id;
        onReorder(active.id, destinationId);
      }}>
      <div className="catalog-list">
        <SortableContext items={generalItems.map((item) => item.id)} strategy={verticalListSortingStrategy}>{generalItems.map((general) => {
          const details = levelItems.filter((item) => item.parentId === general.id);
          return (
            <SortableCatalogItem id={general.id} className="catalog-group" key={general.id}>
              <div className="catalog-general">
                <div><strong>{general.code}</strong><span>{general.label}</span></div>
                <div className="row-actions"><button onClick={() => setExpandedId(expandedId === general.id ? null : general.id)} aria-label={`Afficher les critères de ${general.label}`}>{expandedId === general.id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</button><button onClick={() => edit(general)} aria-label={`Modifier ${general.label}`}><Pencil size={16} /></button><button onClick={() => onDelete(general.id)} aria-label={`Supprimer ${general.label}`}><Trash2 size={16} /></button></div>
              </div>
              {expandedId === general.id && <CriteriaGrid criteria={general.criteria} />}
              {details.length > 0 && <div className="catalog-details"><SortableContext items={details.map((item) => item.id)} strategy={verticalListSortingStrategy}>{details.map((detail) => (
                <SortableCatalogItem id={detail.id} key={detail.id} className="catalog-detail-row"><div className="detail-copy"><ChevronRight size={15} /><strong>{detail.code}</strong><span>{detail.label}</span></div><div className="row-actions"><button onClick={() => setExpandedId(expandedId === detail.id ? null : detail.id)} aria-label={`Afficher les critères de ${detail.label}`}>{expandedId === detail.id ? <ChevronDown size={15} /> : <ChevronRight size={15} />}</button><button onClick={() => edit(detail)} aria-label={`Modifier ${detail.label}`}><Pencil size={15} /></button><button onClick={() => onDelete(detail.id)} aria-label={`Supprimer ${detail.label}`}><Trash2 size={15} /></button></div>{expandedId === detail.id && <CriteriaGrid criteria={detail.criteria} />}</SortableCatalogItem>
              ))}</SortableContext></div>}
            </SortableCatalogItem>
          );
        })}</SortableContext>
        {generalItems.length === 0 && <div className="management-empty">Aucune compétence pour ce niveau.</div>}
      </div>
      </DndContext>
    </main>
  );
}

function CriteriaGrid({ criteria }: { criteria: ValidationCriteria }) {
  return <div className="criteria-grid">{(["A", "B", "C", "D"] as const).map((value) => <div key={value} className={`criterion criterion-${value.toLowerCase()}`}><strong>{value}</strong><p>{criteria[value] || "Critère à définir"}</p></div>)}</div>;
}

type ActivitiesViewProps = {
  activities: ActivityDefinition[];
  catalog: CatalogCompetency[];
  visibleLevels: SchoolLevel[];
  onSave: (activity: ActivityDefinition) => void;
  onDelete: (id: string) => void;
};

type ActivityWithTheme = ActivityDefinition & { theme?: string };
type ActivityUsageStatus = "not-evaluated" | "in-progress" | "evaluated";
type ActivityLevelFilter = "all" | SchoolLevel;
type StoredActivitySession = {
  activityId?: string;
  endedAt?: string;
  evaluations?: Record<string, Record<string, unknown>>;
};

const activityTheme = (activity: ActivityDefinition) => (activity as ActivityWithTheme).theme ?? "";
const activityUsageLabel: Record<ActivityUsageStatus, string> = {
  "not-evaluated": "Non évaluée",
  "in-progress": "En cours",
  evaluated: "Évaluée",
};

function readStoredSessions(): StoredActivitySession[] {
  try {
    const value = JSON.parse(localStorage.getItem("competenceo-sessions-v1") ?? "[]");
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
}

function readCurrentSession(): StoredActivitySession | null {
  try {
    const raw = localStorage.getItem("competenceo-current-session-v1");
    return raw ? JSON.parse(raw) as StoredActivitySession : null;
  } catch {
    return null;
  }
}

function sessionHasEvaluation(session: StoredActivitySession) {
  return Object.values(session.evaluations ?? {}).some((studentResults) => Object.keys(studentResults ?? {}).length > 0);
}

function emptyActivity(schoolLevel: SchoolLevel, curriculum: Curriculum): ActivityWithTheme {
  const firstTheme = curriculumForLevel(curriculum, schoolLevel)[0];
  return {
    id: "",
    title: "",
    schoolLevel,
    theme: firstTheme?.title ?? "",
    chapter: firstTheme?.chapters[0]?.title ?? "",
    coefficient: 1,
    competencyIds: [],
  };
}

export function ActivitiesView({ activities, catalog, visibleLevels, onSave, onDelete }: ActivitiesViewProps) {
  const [curriculum, setCurriculum] = useState<Curriculum>({});
  const [draft, setDraft] = useState<ActivityWithTheme>(() => emptyActivity(visibleLevels[0] ?? "Seconde", {}));
  const [editing, setEditing] = useState(false);
  const [filterLevel, setFilterLevel] = useState<ActivityLevelFilter>("all");
  const [filterTheme, setFilterTheme] = useState("all");
  const [filterChapter, setFilterChapter] = useState("all");
  const available = useMemo(() => orderCatalogForLevel(catalog, draft.schoolLevel), [catalog, draft.schoolLevel]);
  const visibleActivities = useMemo(() => {
    const levelOrder = new Map(visibleLevels.map((item, index) => [item, index]));
    const themeOrders = new Map<SchoolLevel, Map<string, number>>();
    visibleLevels.forEach((level) => {
      themeOrders.set(level, new Map(curriculumForLevel(curriculum, level).map((theme, index) => [theme.title, index])));
    });
    return activities
      .filter((activity) => visibleLevels.includes(activity.schoolLevel))
      .slice()
      .sort((a, b) => {
        const levelDelta = (levelOrder.get(a.schoolLevel) ?? 999) - (levelOrder.get(b.schoolLevel) ?? 999);
        if (levelDelta) return levelDelta;
        const aTheme = activityTheme(a);
        const bTheme = activityTheme(b);
        const themeOrder = themeOrders.get(a.schoolLevel);
        const themeDelta = (themeOrder?.get(aTheme) ?? 999) - (themeOrder?.get(bTheme) ?? 999);
        if (themeDelta) return themeDelta;
        const themeNameDelta = aTheme.localeCompare(bTheme, "fr", { sensitivity: "base" });
        if (themeNameDelta) return themeNameDelta;
        const chapterDelta = a.chapter.localeCompare(b.chapter, "fr", { sensitivity: "base" });
        if (chapterDelta) return chapterDelta;
        return a.title.localeCompare(b.title, "fr", { sensitivity: "base" });
      });
  }, [activities, visibleLevels, curriculum]);
  const filterThemes = useMemo(() => {
    const source = visibleActivities.filter((activity) => filterLevel === "all" || activity.schoolLevel === filterLevel);
    const values = [...new Set(source.map(activityTheme).filter(Boolean))];
    if (filterLevel !== "all") {
      const order = new Map(curriculumForLevel(curriculum, filterLevel).map((theme, index) => [theme.title, index]));
      values.sort((a, b) => (order.get(a) ?? 999) - (order.get(b) ?? 999) || a.localeCompare(b, "fr", { sensitivity: "base" }));
    } else {
      values.sort((a, b) => a.localeCompare(b, "fr", { sensitivity: "base" }));
    }
    return values;
  }, [visibleActivities, filterLevel, curriculum]);
  const filterChapters = useMemo(() => {
    const source = visibleActivities.filter((activity) =>
      (filterLevel === "all" || activity.schoolLevel === filterLevel)
      && (filterTheme === "all" || activityTheme(activity) === filterTheme));
    return [...new Set(source.map((activity) => activity.chapter).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b, "fr", { sensitivity: "base" }));
  }, [visibleActivities, filterLevel, filterTheme]);
  const filteredActivities = useMemo(() => visibleActivities.filter((activity) =>
    (filterLevel === "all" || activity.schoolLevel === filterLevel)
    && (filterTheme === "all" || activityTheme(activity) === filterTheme)
    && (filterChapter === "all" || activity.chapter === filterChapter)),
  [visibleActivities, filterLevel, filterTheme, filterChapter]);
  const groupedActivities = useMemo(() => visibleLevels.flatMap((level) => {
    const levelActivities = filteredActivities.filter((activity) => activity.schoolLevel === level);
    if (!levelActivities.length) return [];
    const themes = new Map<string, ActivityDefinition[]>();
    levelActivities.forEach((activity) => {
      const theme = activityTheme(activity) || "Sans thème";
      themes.set(theme, [...(themes.get(theme) ?? []), activity]);
    });
    return [{ level, themes: [...themes.entries()].map(([theme, items]) => ({ theme, items })) }];
  }), [filteredActivities, visibleLevels]);
  const activityStatuses = useMemo(() => {
    const statuses = new Map<string, ActivityUsageStatus>();
    activities.forEach((activity) => statuses.set(activity.id, "not-evaluated"));
    readStoredSessions().forEach((session) => {
      if (session.activityId && statuses.has(session.activityId) && sessionHasEvaluation(session)) {
        statuses.set(session.activityId, "evaluated");
      }
    });
    const currentSession = readCurrentSession();
    if (currentSession?.activityId && statuses.has(currentSession.activityId)) {
      statuses.set(currentSession.activityId, "in-progress");
    }
    return statuses;
  }, [activities]);
  const programThemes = curriculumForLevel(curriculum, draft.schoolLevel);
  const selectedProgramTheme = programThemes.find((item) => item.title === (draft.theme ?? ""));
  const programChapters = selectedProgramTheme?.chapters ?? [];
  const legacyTheme = Boolean(draft.theme) && !programThemes.some((item) => item.title === draft.theme);
  const legacyChapter = Boolean(draft.chapter) && !programChapters.some((item) => item.title === draft.chapter);
  const hasFilters = filterLevel !== "all" || filterTheme !== "all" || filterChapter !== "all";

  useEffect(() => {
    fetch("/api/curriculum", { credentials: "include" })
      .then((response) => response.ok ? response.json() : null)
      .then((body) => { if (body?.curriculum) setCurriculum(curriculumFromResponse(body)); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    setDraft((current) => ({ ...current, competencyIds: current.competencyIds.filter((id) => available.some((item) => item.id === id)) }));
  }, [draft.schoolLevel]);

  useEffect(() => {
    if (filterLevel !== "all" && !visibleLevels.includes(filterLevel)) {
      setFilterLevel("all");
      setFilterTheme("all");
      setFilterChapter("all");
    }
  }, [filterLevel, visibleLevels]);

  function resetFilters() {
    setFilterLevel("all");
    setFilterTheme("all");
    setFilterChapter("all");
  }

  function startNew() {
    setDraft(emptyActivity(visibleLevels[0] ?? "Seconde", curriculum));
    setEditing(true);
  }

  function edit(activity: ActivityDefinition) {
    setDraft({ ...activity, theme: activityTheme(activity), competencyIds: [...activity.competencyIds] });
    setEditing(true);
  }

  function changeLevel(schoolLevel: SchoolLevel) {
    const next = emptyActivity(schoolLevel, curriculum);
    setDraft((current) => ({ ...current, schoolLevel, theme: next.theme, chapter: next.chapter, competencyIds: [] }));
  }

  function changeTheme(title: string) {
    const nextTheme = programThemes.find((item) => item.title === title);
    setDraft((current) => ({ ...current, theme: title, chapter: nextTheme?.chapters[0]?.title ?? "" }));
  }

  function toggleCompetency(id: string) {
    setDraft((current) => ({ ...current, competencyIds: current.competencyIds.includes(id) ? current.competencyIds.filter((item) => item !== id) : [...current.competencyIds, id] }));
  }

  function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!draft.title.trim() || !draft.theme?.trim() || !draft.chapter.trim() || draft.competencyIds.length === 0) return;
    const selected = new Set(draft.competencyIds);
    const saved = {
      ...draft,
      id: draft.id || makeId("activity"),
      title: draft.title.trim(),
      theme: draft.theme.trim(),
      chapter: draft.chapter.trim(),
      coefficient: Math.max(.1, draft.coefficient),
      competencyIds: available.filter((item) => selected.has(item.id)).map((item) => item.id),
    } as ActivityDefinition;
    onSave(saved);
    setEditing(false);
    setDraft(emptyActivity(visibleLevels[0] ?? "Seconde", curriculum));
  }

  return (
    <main className="management-page">
      <div className="management-heading">
        <div><span className="eyebrow">Préparation des séances</span><h1>Activités</h1></div>
        <button className="primary" onClick={startNew}><Plus size={17} /> Nouvelle activité</button>
      </div>

      <div className="activity-filter-bar" aria-label="Filtrer les activités">
        <PedagogicalLevelFields value={filterLevel} levels={visibleLevels} allLabel="Tous les niveaux" onChange={value=>{setFilterLevel(value as ActivityLevelFilter);setFilterTheme('all');setFilterChapter('all');}} />
        <label><span>Thème</span><select value={filterTheme} onChange={(event) => { setFilterTheme(event.target.value); setFilterChapter("all"); }}><option value="all">Tous les thèmes</option>{filterThemes.map((theme) => <option value={theme} key={theme}>{theme}</option>)}</select></label>
        <label><span>Chapitre</span><select value={filterChapter} onChange={(event) => setFilterChapter(event.target.value)}><option value="all">Tous les chapitres</option>{filterChapters.map((chapter) => <option value={chapter} key={chapter}>{chapter}</option>)}</select></label>
        <button type="button" className="activity-filter-reset" onClick={resetFilters} disabled={!hasFilters}>Réinitialiser</button>
        <small className="activity-filter-count">{filteredActivities.length} activité{filteredActivities.length > 1 ? "s" : ""} affichée{filteredActivities.length > 1 ? "s" : ""}</small>
      </div>

      <div className={`activities-layout ${editing ? "editing" : ""}`}>
        <div className="activity-library">
          {groupedActivities.map((levelGroup) => (
            <section className="activity-level-section" key={levelGroup.level}>
              <div className="activity-level-heading"><strong>{levelGroup.level}</strong><small>{levelGroup.themes.reduce((sum, group) => sum + group.items.length, 0)} activité{levelGroup.themes.reduce((sum, group) => sum + group.items.length, 0) > 1 ? "s" : ""}</small></div>
              {levelGroup.themes.map((themeGroup) => (
                <div className="activity-theme-group" key={`${levelGroup.level}:${themeGroup.theme}`}>
                  <div className="activity-theme-heading"><span>{themeGroup.theme}</span><small>{themeGroup.items.length} activité{themeGroup.items.length > 1 ? "s" : ""}</small></div>
                  <div className="activity-theme-cards">
                    {themeGroup.items.map((activity) => {
                      const usageStatus = activityStatuses.get(activity.id) ?? "not-evaluated";
                      return (
                        <article className="activity-card" key={activity.id} onClick={() => edit(activity)}>
                          <div className="activity-card-icon"><BookOpen size={20} /></div>
                          <div className="activity-card-copy">
                            <span>{activity.schoolLevel} · Coef. {activity.coefficient}</span>
                            <strong>{activity.title}</strong>
                            <p>{activityTheme(activity) ? `${activityTheme(activity)} · ${activity.chapter}` : activity.chapter}</p>
                            <small>{activity.competencyIds.length} compétence{activity.competencyIds.length > 1 ? "s" : ""}</small>
                            <em className={`activity-usage-status status-${usageStatus}`}>{activityUsageLabel[usageStatus]}</em>
                          </div>
                          <button className="delete-activity" onClick={(event) => { event.stopPropagation(); onDelete(activity.id); }} aria-label={`Supprimer ${activity.title}`}><Trash2 size={16} /></button>
                        </article>
                      );
                    })}
                  </div>
                </div>
              ))}
            </section>
          ))}
          {visibleActivities.length === 0 && <div className="management-empty">Aucune activité enregistrée pour tes niveaux.</div>}
          {visibleActivities.length > 0 && filteredActivities.length === 0 && <div className="management-empty">Aucune activité ne correspond à ces filtres.</div>}
        </div>

        {editing && (
          <form className="activity-editor" onSubmit={submit}>
            <div className="form-title"><strong>{draft.id ? "Modifier l’activité" : "Créer une activité"}</strong><div><a href="/programmes">Gérer les programmes</a><button type="button" onClick={() => setEditing(false)}>Fermer</button></div></div>
            <label><span>Titre</span><input value={draft.title} onChange={(event) => setDraft({ ...draft, title: event.target.value })} placeholder="Ex. Mesurer une longueur d’onde" required /></label>
            <div className="form-row"><PedagogicalLevelFields value={draft.schoolLevel} levels={visibleLevels} onChange={value=>changeLevel(value as SchoolLevel)} /><label><span>Coefficient</span><input type="number" min="0.1" step="0.1" value={draft.coefficient} onChange={(event) => setDraft({ ...draft, coefficient: Number(event.target.value) })} /></label></div>
            <label><span>Thème</span>{programThemes.length ? <select value={draft.theme ?? ""} onChange={(event) => changeTheme(event.target.value)}>{legacyTheme && <option value={draft.theme}>{draft.theme} — ancien choix</option>}{programThemes.map((item) => <option key={item.id} value={item.title}>{item.title}</option>)}</select> : <input value={draft.theme ?? ""} onChange={(event) => setDraft({ ...draft, theme: event.target.value })} placeholder="Thème" required />}</label>
            <label><span>Chapitre</span>{programThemes.length ? <select value={draft.chapter} onChange={(event) => setDraft({ ...draft, chapter: event.target.value })}>{legacyChapter && <option value={draft.chapter}>{draft.chapter} — ancien choix</option>}{programChapters.map((item) => <option key={item.id} value={item.title}>{curriculumChapterLabel(item)}</option>)}</select> : <input value={draft.chapter} onChange={(event) => setDraft({ ...draft, chapter: event.target.value })} placeholder="Chapitre" required />}</label>
            <fieldset className="competency-picker"><legend>Compétences évaluées <small>1 à 6 conseillées</small></legend>{available.map((item) => (
              <label key={item.id} className={draft.competencyIds.includes(item.id) ? "checked" : ""}><input type="checkbox" checked={draft.competencyIds.includes(item.id)} onChange={() => toggleCompetency(item.id)} /><span><strong>{item.code}</strong>{item.label}<small>{item.parentId ? "Détaillée" : "Générale"}</small></span>{draft.competencyIds.includes(item.id) && <Check size={17} />}</label>
            ))}</fieldset>
            <button className="primary activity-save" type="submit" disabled={draft.competencyIds.length === 0}><Save size={17} /> Enregistrer l’activité</button>
          </form>
        )}
      </div>
    </main>
  );
}
